import { useTheme } from "@emotion/react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { AMSModules, paginationStyle } from "../../utils/constants";
import { formatDateToMonthDateYear, StatusBadge } from "../../utils/functions";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getRowsPerPageOptions } from "../../utils/functions";
import { makeStyles } from "@mui/styles";
import FundingSourceTableToolbar from "./FundingSourceTableToolbar";
import AddEditFundingSourceDialog, {
	FUNDING_STATUSES,
} from "./AddEditFundingSourceDialog";
import FundingDetailDialog from "./FundingDetailDialog";
import {
	createFundingSource,
	deleteFundingSource,
	getFundingSources,
	updateFundingSource,
} from "../../reducers/FundingSourcesReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import {
	getFundingAdministratorOptions,
	getFundingSourceData,
	getFundingSourceOptions,
} from "./useFundingSourceData";
import {
	getDispensableItems,
	getEquipmentMaintenance,
	getEquipments,
	getVehicles,
	getWorkOrders,
} from "../../utils/commonServices";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	status: [],
	source: [],
	administrator: [],
};

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const FundingSources = () => {
	const classes = useStyles();

	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { fundingSources, loading, total } = useSelector(
		(state) =>
			state.fundingSources || {
				fundingSources: [],
				loading: false,
				total: 0,
			},
	);

	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.fundingSources || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDetailDialog, setOpenDetailDialog] = useState(false);
	const [selectedFundingSource, setSelectedFundingSource] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [sourceOptions, setSourceOptions] = useState([]);
	const [administratorOptions, setAdministratorOptions] = useState([]);
	const [workOrders, setWorkOrders] = useState([]);
	const [vehicles, setVehicles] = useState([]);
	const [equipment, setEquipment] = useState([]);
	const [maintenance, setMaintenance] = useState([]);
	const [dispensableItems, setDispensableItems] = useState([]);

	const currentAgency = useRef(null);

	const handleSaveOptions = async () => {
		const sourceOpts = await getFundingSourceOptions(userAgency);
		const administratorOpts =
			await getFundingAdministratorOptions(userAgency);
		const woList = await getWorkOrders(userAgency);
		const vehiList = await getVehicles(userAgency);
		const equList = await getEquipments(userAgency);
		const mainList = await getEquipmentMaintenance(userAgency);
		const desList = await getDispensableItems(userAgency);

		setSourceOptions(sourceOpts);
		setAdministratorOptions(administratorOpts);
		setWorkOrders(woList);
		setVehicles(vehiList);
		setEquipment(equList);
		setMaintenance(mainList);
		setDispensableItems(desList);
	};

	useEffect(() => {
		if (userAgency) {
			handleSaveOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getFundingSources(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	const calcRemaining = (f) => {
		const spentDisp = (dispensableItems || []).reduce((s, i) => {
			if (i.fundingSourceID !== f.id) return s;
			return (
				s +
				(i.issuances || [])
					.filter((iss) => iss.isRestock && iss.purchaseCost != null)
					.reduce((a, iss) => a + (iss.purchaseCost || 0), 0)
			);
		}, 0);
		const spent =
			(vehicles || [])
				.filter((v) => v.fundingSourceID === f.id)
				.reduce((s, v) => s + (v.originalCost || 0), 0) +
			(equipment || [])
				.filter((e) => e.fundingSourceID === f.id)
				.reduce((s, e) => s + (e.cost || 0), 0) +
			(workOrders || [])
				.filter((w) => w.fundingSourceID === f.id)
				.reduce((s, w) => s + (w.cost || 0), 0) +
			(maintenance || [])
				.filter((m) => m.fundingSourceID === f.id)
				.reduce((s, m) => s + (m.cost || 0), 0) +
			spentDisp;
		return (f.amount || 0) - spent;
	};

	const baseColumns = useMemo(
		() => [
			{
				field: "title",
				headerName: "Title",
				width: 200,
				sortable: true,
			},
			{
				field: "amount",
				headerName: "Amount",
				width: 150,
				sortable: true,
			},
			{
				field: "remaining",
				headerName: "Remaining",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					const remaining = calcRemaining(row);
					return remaining || "-";
				},
			},
			{
				field: "source",
				headerName: "Source",
				width: 150,
				sortable: true,
			},
			{
				field: "administrator",
				headerName: "Administrator",
				width: 150,
				sortable: true,
			},
			{
				field: "startDate",
				headerName: "Start",
				width: 150,
				sortable: true,
				valueFormatter: (params) => {
					return formatDateToMonthDateYear(params.value);
				},
			},
			{
				field: "endDate",
				headerName: "End",
				width: 150,
				sortable: true,
				valueFormatter: (params) => {
					return formatDateToMonthDateYear(params.value);
				},
			},
			{
				field: "status",
				headerName: "Status",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.status} />;
				},
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Funding Source"
											onClick={() => {
												setSelectedFundingSource(row);
												getFundingSourceData(
													row?.id,
												).then((resp) => {
													setSelectedFundingSource(
														resp?.data?.[0] || row,
													);
												});
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Funding Source"
											onClick={() => {
												setSelectedId(row.id);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["fundingSourceTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["fundingSourceTableColumnOrder"]),
				);
			}

			if (settingsMap["fundingSourceTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["fundingSourceTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["fundingSourceTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(
						settingsMap["fundingSourceTableColumnVisibility"],
					),
				);
			}

			if (settingsMap["fundingSourceTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["fundingSourceTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"fundingSourceTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch();
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"fundingSourceTableColumnOrder",
					JSON.stringify(newColumnOrder),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"fundingSourceTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"fundingSourceTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.FundingSources, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		setSearch("");
		dispatch({
			type: AMSModules.FundingSources,
			payload: initialFilterState,
		});
	};

	const handleSaveFundingSource = async (payload) => {
		try {
			if (selectedFundingSource) {
				await dispatch(
					updateFundingSource(selectedFundingSource.id, payload),
				);
			} else {
				await dispatch(createFundingSource(payload));
			}
		} catch (error) {
			dispatch(notify("Failed to create funding source", "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await dispatch(
				deleteFundingSource(selectedId, userData.user.ptsUserID),
			);
			dispatch(
				getFundingSources(
					page,
					pageSize,
					sortModel[0]?.field || "id",
					sortModel[0]?.sort || "asc",
					filters,
					userAgency,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
		} catch (error) {
			dispatch(notify("Failed to delete funding source", "error"));
		}
	};

	const fmtCurrency = (n) =>
		isNaN(n) || n == null ? "—" : `$${Number(n).toLocaleString()}`;

	// Summary stats
	const today = new Date();
	const active = fundingSources.filter((f) => f.status === "Active");
	const pending = fundingSources.filter((f) => f.status === "Pending");
	const totalActive = active.reduce((s, f) => s + (f.amount || 0), 0);
	const expiringSoon = active.filter((f) => {
		if (!f.endDate) return false;
		const d = Math.ceil((new Date(f.endDate) - today) / 86400000);
		return d >= 0 && d <= 60;
	});

	return (
		<>
			<div className="funding-stats">
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div className="funding-stat-num green">
						{fmtCurrency(totalActive)}
					</div>
					<div className="funding-stat-label">Active Funding</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div className="funding-stat-num">{active.length}</div>
					<div className="funding-stat-label">Active Sources</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div
						className={`funding-stat-num ${pending.length ? "yellow" : ""}`}
					>
						{pending.length}
					</div>
					<div className="funding-stat-label">Pending</div>
				</div>
				<div
					className={`funding-stat ${classes.cardBackground} ${classes.colorTitle}`}
				>
					<div
						className={`funding-stat-num ${expiringSoon.length ? "red" : ""}`}
					>
						{expiringSoon.length}
					</div>
					<div className="funding-stat-label">Expiring in 60d</div>
				</div>
			</div>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)",
					marginTop: theme.spacing(0.5),
				}}
			>
				<FundingSourceTableToolbar
					fundingSources={fundingSources}
					dispensableItems={dispensableItems}
					vehicles={vehicles}
					equipment={equipment}
					workOrders={workOrders}
					maintenance={maintenance}
					openAdd={() => setOpenDialog(true)}
					sourceOptions={sourceOptions}
					administratorOptions={administratorOptions}
					search={search}
					setSearch={setSearch}
					status={FUNDING_STATUSES}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={fundingSources}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						getRowId={(row) => row.GUID}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						onRowClick={(params) => {
							setOpenDetailDialog(true);
							setSelectedFundingSource(params?.row);
						}}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>

			<AddEditFundingSourceDialog
				open={openDialog}
				initialData={selectedFundingSource}
				onClose={() => {
					setSelectedFundingSource(null);
					setOpenDialog(false);
				}}
				onSave={handleSaveFundingSource}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Funding Source"
				message="Are you sure you want to delete this funding source?"
			/>

			<FundingDetailDialog
				open={openDetailDialog}
				onClose={() => {
					setOpenDetailDialog(false);
					setSelectedFundingSource(null);
				}}
				record={selectedFundingSource}
				onEdit={(record) => {
					setOpenDetailDialog(false);
					getFundingSourceData(record?.id).then((resp) => {
						setSelectedFundingSource(resp?.data?.[0] || record);
					});
					setOpenDialog(true);
				}}
			/>
		</>
	);
};

export default FundingSources;
